import { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { CheckMarkFillLegacy } from '@lyearn/icons';
import { Avatar, Checkbox, CircularProgress, Stack, Typography } from '@lyearn/ui';
import { UserType } from '@/types/schema';

import { SelectedParticipantsType } from '../..';

type UserListItemProps = {
  user: Pick<UserType, 'name' | 'profilePicture' | 'id' | 'emailId'>;
  progress?: number;
  onChange: () => void;
  selectedParticipants: SelectedParticipantsType;
};

const UserListItem = (props: UserListItemProps) => {
  const { user, progress, onChange, selectedParticipants } = props;
  const { t } = useTranslation();

  const isSelected = useMemo(() => {
    const { isSelectAll, userIds, excludeUserIds } = selectedParticipants;
    return isSelectAll ? !excludeUserIds.includes(user.id) : userIds.includes(user.id);
  }, [selectedParticipants, user.id]);

  const renderStatus = () => {
    return progress === 100 ? (
      <Stack alignItems="center" direction="row">
        <CheckMarkFillLegacy fillColorToken="icon-success" height={20} width={20} />
        <Typography className="text-text-secondary pl-4" variant="body-short-02">
          {t('Common:Filter.Completed')}
        </Typography>
      </Stack>
    ) : (
      <Stack alignItems="center" direction="row">
        <CircularProgress color="success" size="x-small" value={progress} />
        <Typography className="pl-8 text-text-secondary" variant="body-short-02">
          {Math.round((progress ?? 0) * 100) / 100}%
        </Typography>
      </Stack>
    );
  };

  return (
    <Stack className="flex w-full py-16 h-full" direction="row">
      <Stack className="w-full justify-between" direction="row">
        <Stack>
          <Avatar size="xx-large" src={user.profilePicture ?? ''} />
          <Stack className="pl-10" direction="col">
            <Typography variant="body-short-01">{user.name}</Typography>
            <Typography className="text-text-secondary" variant="body-short-02">
              {user.emailId}
            </Typography>
          </Stack>
        </Stack>
        <Stack alignItems="center" direction="row">
          {renderStatus()}
          <Stack className="pl-16">
            <Checkbox checked={isSelected} onChange={onChange} />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default UserListItem;
